<script setup lang="ts">
import { computed, ref } from 'vue';

import type { FormGroup, FormSchema, VFormRef } from '@modules/forms/components/VForm/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useQueryClient } from '@tanstack/vue-query';

import type { ITrackerChangeCountryOptions, ITrackerTrafficSourceComposableOptions } from '../../types';
import type { ICreateEditTrackerEmits, ICreateEditTrackerProps, ITrackerCreateEditForm } from './types';
import { useTrackerChangeCountry, useTrackerTrafficSource } from '../../composables';
import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS } from '../../constants';
import { useCreateEditTrackerMutation } from '../../mutations';

const {
  affId,
  trackerId,
  trackerDescription,
  trackersListQueryKey,
  isTrackerCreating,
} = defineProps<ICreateEditTrackerProps>();

const emit = defineEmits<ICreateEditTrackerEmits>();

const { getTranslation } = useI18n();
const queryClient = useQueryClient();

const visible = defineModel<boolean>('visible');

const form = ref<VFormRef>();

const {
  mutateAsync,
  isLoading,
  errors,
  clearError,
  clearAllErrors,
} = useCreateEditTrackerMutation();

const shortcodePart = computed(() => (trackerId ? 'EDIT_TRACKER_' : 'CREATE_TRACKER_'));

const trackerCountryFormOptions = computed<ITrackerChangeCountryOptions>(() => ({
  affId,
  trackerId,
  isQueryEnabled: visible.value && isTrackerCreating,
}));

const {
  trackerCountryFormSchema,
  updateCountryPermission,
  showSavingCountryWarning,
  onChange: onTrackerCountryChange,
} = useTrackerChangeCountry(trackerCountryFormOptions);

const trafficSourceFormOptions = computed<ITrackerTrafficSourceComposableOptions>(() => ({
  affiliateId: affId,
  withoutDefault: true,
  enableQuery: visible.value && isTrackerCreating,
}));

const {
  formTrafficSourceSchema,
  formGroups: trafficSourceFormGroups,
  changeTrafficSourcePermission,
  showTrafficSourceWarning,
  onChange: onTrafficSourceChange,
} = useTrackerTrafficSource(trafficSourceFormOptions);

const isTrafficSource = computed<boolean>(() => isTrackerCreating && changeTrafficSourcePermission.value);
const isCountryField = computed<boolean>(() => isTrackerCreating && updateCountryPermission.value);

const formSchema = computed<FormSchema<ITrackerCreateEditForm>>(() => {
  const schema: FormSchema<ITrackerCreateEditForm> = {
    description: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, `${shortcodePart.value}DESCRIPTION`).value,
      value: trackerDescription,
      required: true,
    },
  };

  if (isCountryField.value) {
    Object.assign(schema, trackerCountryFormSchema.value);
  }

  if (isTrafficSource.value) {
    Object.assign(schema, formTrafficSourceSchema.value);
  }

  return schema;
});

const trackerCountryFormGroups = ['country_code'] as const;

const formGroups = computed<FormGroup<ITrackerCreateEditForm>[]>(() => trafficSourceFormGroups.value.map((item) => {
  return {
    ...item,
    items: [
      ...(isTrafficSource.value ? item.items : []),
      ...(isCountryField.value ? trackerCountryFormGroups : []),
      'description',
    ],
  };
}));

function onChange(key: keyof ITrackerCreateEditForm, value: ITrackerCreateEditForm[keyof ITrackerCreateEditForm]): void {
  if (key === 'traffic_source_id') {
    onTrafficSourceChange(key, value);
  }
  if (key === 'country_code') {
    onTrackerCountryChange(key, value);
  }
}

async function onSubmit({ description, traffic_source_id, country_code }: ITrackerCreateEditForm): Promise<void> {
  await mutateAsync({
    id: affId,
    tracker: {
      id: trackerId,
      description,
      country_code,
      traffic_source_id,
    },
  });

  if (trackersListQueryKey) {
    void queryClient.invalidateQueries({ queryKey: [trackersListQueryKey] });
  }

  close();
}
function onFormSubmit(): void {
  form.value?.submit();
}

function onHide(): void {
  showTrafficSourceWarning.value = false;
  showSavingCountryWarning.value = false;
  clearAllErrors();
  emit('hide');
}

function close(): void {
  visible.value = false;
}
</script>

<template>
  <VDialog
    v-model:visible="visible"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, `${shortcodePart}TITLE`)"
    @hide="onHide"
  >
    <VForm
      ref="form"
      :schema="formSchema"
      :groups="formGroups"
      :is-loading="isLoading"
      :custom-errors="errors"
      @change="onChange"
      @submit="onSubmit"
      @interacted="clearError"
    />
    <template #footer>
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, `${shortcodePart}CANCEL`)"
        outlined
        @click="close"
      />
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, `${shortcodePart}SAVE`)"
        :loading="isLoading"
        @click="onFormSubmit"
      />
    </template>
  </VDialog>
</template>
